import { EVMProviderOptions } from '@lifi/sdk'
import { createWalletClient, http, keccak256, parseSignature, serializeTransaction } from 'viem'
import { toAccount } from 'viem/accounts'
import * as chains from 'viem/chains'
import { mainnet } from 'viem/chains'
import { signEthHash } from '../../handlers/walletActions/wallet'
import { EVMChain } from '../../interfaces/common'
import { Wallet } from '../../providers/WalletProvider'
import { getEthProvider } from '../../utils/getEthProvider'

const viemEvmChains = Object.values(chains)

export const getEvmProviderOptions = (
  wallet: Wallet,
  evmChains: EVMChain[]
): EVMProviderOptions => {
  const address = wallet.ethAddress as `0x${string}`
  const ethChain = evmChains.find((c) => c.chainId === 1)

  if (!address || !ethChain) {
    throw new Error('Missing ETH address or chain')
  }

  const account = toAccount({
    address,
    async signTransaction(tx, options) {
      const evmChain = evmChains.find((c) => c.chainId === tx.chainId)!
      const provider = getEthProvider(evmChain.rpcEndpoint)

      const [gas, nonce] = await Promise.all([
        provider.estimateFeesPerGas(),
        provider.getTransactionCount({ address, blockTag: 'pending' }),
      ])

      tx.maxFeePerGas = gas.maxFeePerGas
      tx.maxPriorityFeePerGas = gas.maxPriorityFeePerGas
      tx.nonce = nonce

      const serialize = options?.serializer || serializeTransaction
      const serializedTx = await serialize(tx)
      const hash = keccak256(serializedTx)
      const signature = await signEthHash(wallet.principalId, hash as `0x${string}`)

      return serialize(tx, parseSignature(signature))
    },
    signMessage() {
      throw new Error('signMessage is not implemented')
    },
    signTypedData() {
      throw new Error('signTypedData is not implemented')
    },
  })

  return {
    getWalletClient: async () => {
      return createWalletClient({
        account,
        chain: mainnet,
        transport: http(ethChain.rpcEndpoint),
      })
    },
    switchChain: async (chainId) => {
      const plugEvmChain = evmChains.find((c) => c.chainId === chainId)
      const viemEvmChain = viemEvmChains.find((c) => c.id === chainId)

      if (!plugEvmChain || !viemEvmChain) {
        throw new Error(`Unsupported EVM chain (chain ID = ${chainId})`)
      }

      return createWalletClient({
        account,
        chain: viemEvmChain,
        transport: http(plugEvmChain.rpcEndpoint),
      })
    },
  }
}
